import { db } from '../firebase';
import { doc, setDoc, getDoc, updateDoc, increment, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth } from '../firebase';

export interface UserData {
  createdAt: string;
  email: string;
  isPremium: boolean;
  lastAccessedYear: string;
  name: string;
  questionAttempts: number;
  questionsAttempted: number;
}

export const initializeUserData = async (userId: string, email: string) => {
  const userRef = doc(db, 'users', userId);
  
  const userData: UserData = {
    createdAt: new Date().toISOString(),
    email: email,
    isPremium: false,
    lastAccessedYear: '2015',
    name: email.split('@')[0],
    questionAttempts: 0,
    questionsAttempted: 0
  };

  await setDoc(userRef, userData);
};

export const incrementAttemptedQuestions = async (userId: string) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const currentAttempts = userDoc.data()?.questionAttempts ?? 0;

    // Only increment if under 5 attempts for free users
    if (currentAttempts < 5 || userDoc.data()?.isPremium) {
      await updateDoc(userRef, {
        questionAttempts: increment(1)
      });
    }
  } catch (error) {
    console.error('Error incrementing attempted questions:', error);
  }
};

export const getUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Get email from auth
      const user = auth.currentUser;
      if (!user?.email) return null;
      
      await initializeUserData(userId, user.email);
      return {
        createdAt: new Date().toISOString(),
        email: user.email,
        isPremium: false,
        lastAccessedYear: '2015',
        name: user.email.split('@')[0],
        questionAttempts: 0,
        questionsAttempted: 0
      };
    }

    return userSnap.data() as UserData;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const setUserPremium = async (email: string, paymentDetails: any) => {
  try {
    const userRef = doc(db, 'users', email);
    
    await updateDoc(userRef, {
      isPremium: true,
      paymentDetails: {
        ...paymentDetails,
        activatedAt: serverTimestamp()
      }
    });

    return true;
  } catch (error) {
    console.error('Error updating premium status:', error);
    throw error;
  }
};

// Optional: Add real-time listener for user data
export const subscribeToUserData = (
  email: string, 
  callback: (data: UserData | null) => void
) => {
  const userRef = doc(db, 'users', email);
  
  return onSnapshot(userRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data() as UserData);
    } else {
      callback(null);
    }
  });
};

export const updateUserQuestionAttempts = async (email: string, attempts: number) => {
  try {
    const userRef = doc(db, 'users', email);
    await updateDoc(userRef, {
      questionAttempts: attempts
    });
  } catch (error) {
    console.error('Error updating question attempts:', error);
    throw error;
  }
}; 